// TS bridge to the custom HealthBridge Swift plugin.
//
// Read-only HealthKit access for trip stats. Web is a no-op; pre-plugin
// native builds return null. Callers MUST treat null/zero as "no data" and
// not block any flow on Health being available — HK is enrichment, never
// required.
//
// Permissions: call `requestHealthAuth()` once from a user-initiated UI
// (e.g. a "Show step data" toggle on the trip stats card). HK deliberately
// hides per-type read authorization, so the only way to know if data is
// available is to query and see if the result is non-zero.
import { registerPlugin, Capacitor } from "@capacitor/core";
import { isPluginRegistered, probePlugin, type PluginProbeResult } from "./plugin-inventory";

export type HealthAuthStatus =
  | "unavailable"
  | "prompt"
  | "denied_write"
  | "authorized_write"
  | "unknown";

export interface HealthTrackPoint {
  lat: number;
  lng: number;
  timestampMs: number;
  horizontalAccuracy?: number;
  altitude?: number;
  speed?: number;
  course?: number;
}

export type HealthActivityType = "walking" | "hiking" | "running" | "cycling" | "other";

export interface HealthBridgePlugin {
  isAvailable(): Promise<{ available: boolean }>;
  requestAuth(opts?: { includeWrite?: boolean }): Promise<{ ok: boolean; error?: string }>;
  getAuthStatus(): Promise<{ status: HealthAuthStatus }>;
  getStepsForRange(opts: { startMs: number; endMs: number }): Promise<{
    ok: boolean;
    steps: number;
    error?: string;
  }>;
  getDistanceForRange(opts: { startMs: number; endMs: number }): Promise<{
    ok: boolean;
    meters: number;
    error?: string;
  }>;
  saveTrackAsWorkout(opts: {
    startMs: number;
    endMs: number;
    activityType?: HealthActivityType;
    distanceMeters?: number;
    points: HealthTrackPoint[];
  }): Promise<{ ok: boolean; uuid?: string; routeOk?: boolean; routeError?: string; error?: string }>;
}

export const HealthBridge = registerPlugin<HealthBridgePlugin>("HealthBridge");

/** True when the Swift plugin is compiled into this build (registration hint only). */
export function healthBridgeRegistered(): boolean {
  return isPluginRegistered("HealthBridge");
}

/** Full diagnostic via a safe `isAvailable()` probe. */
export async function probeHealthBridge(): Promise<PluginProbeResult> {
  return probePlugin("HealthBridge", () => HealthBridge.isAvailable(), "isAvailable()");
}

/** Web/native-safe: returns false on web or when HK is unavailable. */
export async function isHealthAvailable(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    const r = await HealthBridge.isAvailable();
    return !!r?.available;
  } catch {
    return false;
  }
}

/** Prompt for read access to steps + walking/running distance. */
export async function requestHealthAuth(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    const r = await HealthBridge.requestAuth();
    return !!r?.ok;
  } catch {
    return false;
  }
}

/** Sum of step count between two timestamps (ms epoch). Returns null on failure. */
export async function getStepsForRange(startMs: number, endMs: number): Promise<number | null> {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const r = await HealthBridge.getStepsForRange({ startMs, endMs });
    return r?.ok ? Math.round(r.steps ?? 0) : null;
  } catch {
    return null;
  }
}

/** Sum of walking/running distance in meters between two timestamps. Returns null on failure. */
export async function getWalkingDistanceMetersForRange(
  startMs: number,
  endMs: number,
): Promise<number | null> {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const r = await HealthBridge.getDistanceForRange({ startMs, endMs });
    return r?.ok ? r.meters ?? 0 : null;
  } catch {
    return null;
  }
}

/** Prompt for write access (workouts + route + distance). Idempotent on iOS. */
export async function requestHealthWriteAuth(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    const r = await HealthBridge.requestAuth({ includeWrite: true });
    return !!r?.ok;
  } catch {
    return false;
  }
}

export interface SaveTrackResult {
  ok: boolean;
  uuid?: string;
  routeOk?: boolean;
  error?: string;
}

/**
 * Save a trip's GPS track as an HKWorkout + HKWorkoutRoute. Web/no-plugin
 * returns { ok: false, error: "unsupported" }. Caller MUST persist the
 * returned `uuid` to avoid double-writes — re-calling will create a new
 * workout in Health.
 */
export async function saveTrackAsWorkout(opts: {
  startMs: number;
  endMs: number;
  activityType?: HealthActivityType;
  distanceMeters?: number;
  points: HealthTrackPoint[];
}): Promise<SaveTrackResult> {
  if (!Capacitor.isNativePlatform()) {
    return { ok: false, error: "unsupported" };
  }
  if (!opts.points || opts.points.length < 2) {
    return { ok: false, error: "not_enough_points" };
  }
  try {
    const r = await HealthBridge.saveTrackAsWorkout(opts);
    return {
      ok: !!r?.ok,
      uuid: r?.uuid,
      routeOk: r?.routeOk,
      error: r?.error,
    };
  } catch (e) {
    return { ok: false, error: (e as Error)?.message ?? "save_failed" };
  }
}
